import { oc } from "@orpc/contract";
import { z } from "zod";
import { ResourceOriginSchema } from "./voice-agent-configs.contract";

export const PhoneNumberProvider = {
  Vapi: "vapi",
  Twilio: "twilio",
} as const;

export const PhoneNumberProviderSchema = z.enum([
  PhoneNumberProvider.Vapi,
  PhoneNumberProvider.Twilio,
]);

export type PhoneNumberProviderValue = z.infer<
  typeof PhoneNumberProviderSchema
>;

export const VoicePhoneNumberSchema = z.object({
  id: z.uuid(),
  organizationId: z.uuid(),
  provider: PhoneNumberProviderSchema,
  providerId: z.string().min(1),
  number: z.string().nullable(),
  name: z.string().nullable(),
  origin: ResourceOriginSchema,
  status: z.string().nullable(),
  createdAt: z.coerce.date(),
  updatedAt: z.coerce.date(),
});

export type VoicePhoneNumberDTO = z.infer<typeof VoicePhoneNumberSchema>;

export const CreateVoicePhoneNumberInputSchema = z.object({
  provider: PhoneNumberProviderSchema,
  providerId: z.string().min(1),
  origin: ResourceOriginSchema,
  number: z.string().nullable().optional(),
  name: z.string().nullable().optional(),
  status: z.string().nullable().optional(),
});

export type CreateVoicePhoneNumberInput = z.infer<
  typeof CreateVoicePhoneNumberInputSchema
>;

export const UpdateVoicePhoneNumberInputSchema = z.object({
  id: z.uuid(),
  name: z.string().nullable().optional(),
  status: z.string().nullable().optional(),
});

export type UpdateVoicePhoneNumberInput = z.infer<
  typeof UpdateVoicePhoneNumberInputSchema
>;

const listVoicePhoneNumbers = oc.output(z.array(VoicePhoneNumberSchema)).route({
  summary: "List voice phone numbers",
  description:
    "Returns the organization's registered voice phone numbers with provider, number, name, status and whether each was created or linked.",
  method: "GET",
});

const getVoicePhoneNumber = oc
  .input(z.object({ id: z.uuid() }))
  .output(VoicePhoneNumberSchema)
  .route({
    summary: "Get voice phone number",
    description:
      "Returns one of the organization's registered voice phone numbers by id.",
    method: "GET",
  });

const createVoicePhoneNumber = oc
  .input(CreateVoicePhoneNumberInputSchema)
  .output(VoicePhoneNumberSchema)
  .route({
    summary: "Register voice phone number",
    description:
      "Records a provider phone number for the organization, or returns the existing record for the same provider number. Does not buy a number.",
  });

const updateVoicePhoneNumber = oc
  .input(UpdateVoicePhoneNumberInputSchema)
  .output(VoicePhoneNumberSchema)
  .route({
    summary: "Update voice phone number",
    description:
      "Changes the name or status of a registered voice phone number.",
  });

const deleteVoicePhoneNumber = oc
  .input(z.object({ id: z.uuid() }))
  .output(z.object({ success: z.boolean() }))
  .route({
    summary: "Remove voice phone number record",
    description:
      "Deletes a voice phone number record without releasing it at the provider. Blocked while a voice agent configuration uses it.",
  });

const releaseVoicePhoneNumber = oc
  .input(z.object({ id: z.uuid() }))
  .output(z.object({ success: z.boolean(), releasedFromProvider: z.boolean() }))
  .route({
    summary: "Release voice phone number",
    description:
      "Releases the number at its provider and deletes the record. Blocked while a voice agent configuration uses it; reports whether the provider release succeeded.",
  });

export const voicePhoneNumberContracts = {
  list: listVoicePhoneNumbers,
  get: getVoicePhoneNumber,
  create: createVoicePhoneNumber,
  update: updateVoicePhoneNumber,
  delete: deleteVoicePhoneNumber,
  release: releaseVoicePhoneNumber,
} as const;

export type VoicePhoneNumberContracts = typeof voicePhoneNumberContracts;
